import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Bitcoin, Wallet, TrendingUp, Globe, ClipboardList, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CryptoTab() {
  const [cryptoPayments, setCryptoPayments] = useState([]);
  const [cryptoStats, setCryptoStats] = useState({
    total_payments: 0, total_usd: 0, by_network: {}, by_token: {}
  });

  useEffect(() => {
    fetchCryptoPayments();
  }, []);

  const fetchCryptoPayments = async () => {
    try {
      const response = await axios.get(`${API}/crypto/payments?limit=100`);
      const payments = response.data || [];
      setCryptoPayments(payments);
      const stats = {
        total_payments: payments.length,
        total_usd: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
        by_network: {}, by_token: {}
      };
      payments.forEach(p => {
        if (!stats.by_network[p.network]) stats.by_network[p.network] = { count: 0, total: 0 };
        stats.by_network[p.network].count++;
        stats.by_network[p.network].total += p.amount || 0;
        if (!stats.by_token[p.token]) stats.by_token[p.token] = { count: 0, total: 0 };
        stats.by_token[p.token].count++;
        stats.by_token[p.token].total += p.amount || 0;
      });
      setCryptoStats(stats);
    } catch (error) {
      console.error("Failed to fetch crypto payments:", error);
    }
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight flex items-center gap-3">
              <Bitcoin className="w-8 h-8 text-vault-gold" />Crypto Payments
            </h1>
            <p className="text-vault-text-secondary font-manrope mt-1">Track cryptocurrency payments from Bitsy bookings</p>
          </div>
          <Button onClick={fetchCryptoPayments} className="bg-vault-gold hover:bg-amber-500 text-black">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-vault-gold/20 rounded-lg flex items-center justify-center"><Wallet className="w-5 h-5 text-vault-gold" /></div>
              <div><p className="text-vault-text-secondary text-xs">Total Payments</p><p className="text-vault-text text-2xl font-bold">{cryptoStats.total_payments}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-500" /></div>
              <div><p className="text-vault-text-secondary text-xs">Total USD Value</p><p className="text-vault-text text-2xl font-bold">${cryptoStats.total_usd.toFixed(2)}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center"><Globe className="w-5 h-5 text-purple-500" /></div>
              <div><p className="text-vault-text-secondary text-xs">Networks Used</p><p className="text-vault-text text-2xl font-bold">{Object.keys(cryptoStats.by_network).length}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-vault-surface border-vault-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center"><Bitcoin className="w-5 h-5 text-blue-500" /></div>
              <div><p className="text-vault-text-secondary text-xs">Token Types</p><p className="text-vault-text text-2xl font-bold">{Object.keys(cryptoStats.by_token).length}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {cryptoPayments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="bg-vault-surface border-vault-border">
            <CardHeader className="border-b border-vault-border pb-3">
              <CardTitle className="text-sm font-medium text-vault-text flex items-center gap-2"><Globe className="w-4 h-4 text-vault-gold" />By Network</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {Object.entries(cryptoStats.by_network).map(([network, data]) => (
                  <div key={network} className="flex justify-between items-center">
                    <span className="text-vault-text capitalize">{network}</span>
                    <span className="text-vault-gold font-medium">{data.count} payment(s) - ${data.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-vault-surface border-vault-border">
            <CardHeader className="border-b border-vault-border pb-3">
              <CardTitle className="text-sm font-medium text-vault-text flex items-center gap-2"><Bitcoin className="w-4 h-4 text-vault-gold" />By Token</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {Object.entries(cryptoStats.by_token).map(([token, data]) => (
                  <div key={token} className="flex justify-between items-center">
                    <span className="text-vault-text">{token}</span>
                    <span className="text-vault-gold font-medium">{data.count} payment(s) - ${data.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-vault-surface border-vault-border mb-6">
        <CardHeader className="border-b border-vault-border pb-3">
          <CardTitle className="text-sm font-medium text-vault-text flex items-center gap-2"><Wallet className="w-4 h-4 text-vault-gold" />Your Wallet Addresses</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="bg-vault-bg p-3 rounded-lg"><p className="text-vault-text-secondary mb-1">EVM (Polygon, Base, Arbitrum, Ethereum)</p><p className="text-vault-gold font-mono break-all">0xC7F5378a1B801a8f384f8e3759D0242dd5cE650A</p></div>
            <div className="bg-vault-bg p-3 rounded-lg"><p className="text-vault-text-secondary mb-1">Bitcoin</p><p className="text-vault-gold font-mono break-all">bc1qr0k84wsj5ckk7ln483ywera7n0py6pt0zar3u2</p></div>
            <div className="bg-vault-bg p-3 rounded-lg"><p className="text-vault-text-secondary mb-1">Solana</p><p className="text-vault-gold font-mono break-all">Fk5jFAsDWGBg8UPtBWZSqVjc59uiPS3A76WfxtQtybRa</p></div>
            <div className="bg-vault-bg p-3 rounded-lg"><p className="text-vault-text-secondary mb-1">Tron</p><p className="text-vault-gold font-mono break-all">TUYUQMd1u1J95q3JjriwaZLbaigXMu9ppc</p></div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-vault-surface border-vault-border">
        <CardHeader className="border-b border-vault-border">
          <CardTitle className="text-sm font-medium text-vault-text flex items-center gap-2"><ClipboardList className="w-4 h-4 text-vault-gold" />Payment History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {cryptoPayments.length === 0 ? (
            <div className="p-8 text-center">
              <Bitcoin className="w-12 h-12 text-vault-text-secondary mx-auto mb-3 opacity-50" />
              <p className="text-vault-text-secondary">No crypto payments recorded yet</p>
              <p className="text-vault-text-secondary text-sm mt-1">Payments will appear here when guests pay via Bitsy</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-vault-bg">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs text-vault-gold uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs text-vault-gold uppercase tracking-wider">Guest</th>
                    <th className="px-4 py-3 text-left text-xs text-vault-gold uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs text-vault-gold uppercase tracking-wider">Token</th>
                    <th className="px-4 py-3 text-left text-xs text-vault-gold uppercase tracking-wider">Network</th>
                    <th className="px-4 py-3 text-left text-xs text-vault-gold uppercase tracking-wider">TX Hash</th>
                    <th className="px-4 py-3 text-left text-xs text-vault-gold uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-vault-border">
                  {cryptoPayments.map((payment, idx) => (
                    <tr key={payment.id || idx} className="hover:bg-vault-surface-highlight/50">
                      <td className="px-4 py-3 text-sm text-vault-text">{new Date(payment.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm text-vault-text">{payment.guest_name || 'Anonymous'}</td>
                      <td className="px-4 py-3 text-sm text-vault-gold font-medium">${payment.amount?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-vault-text"><span className="px-2 py-1 bg-vault-bg rounded text-xs font-medium">{payment.token}</span></td>
                      <td className="px-4 py-3 text-sm text-vault-text capitalize">{payment.network}</td>
                      <td className="px-4 py-3 text-sm text-vault-text-secondary font-mono">
                        <a href={`https://${payment.network === 'bitcoin' ? 'mempool.space' : payment.network === 'solana' ? 'solscan.io' : payment.network === 'tron' ? 'tronscan.org/#' : payment.network === 'polygon' ? 'polygonscan.com' : payment.network === 'base' ? 'basescan.org' : payment.network === 'arbitrum' ? 'arbiscan.io' : 'etherscan.io'}/tx/${payment.tx_hash}`} target="_blank" rel="noopener noreferrer" className="text-vault-gold hover:underline">
                          {payment.tx_hash?.slice(0, 8)}...{payment.tx_hash?.slice(-6)}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${payment.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' : payment.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>
                          {payment.status || 'confirmed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
